import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, TrendingUp, Briefcase, Zap, GraduationCap, Mic, User, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import ScrollIndicator from "@/components/home/ScrollIndicator";
import GridBackground from "@/components/ui/grid-background";
import GlowCard from "@/components/ui/glow-card";
import AnimatedCounter from "@/components/ui/animated-counter";
import TypewriterHeadline from "@/components/ui/typewriter-headline";
import LogoStrip from "@/components/home/LogoStrip";
import TestimonialCarousel, { type TestimonialItem } from "@/components/home/TestimonialCarousel";

// Lazy load non-critical components (below fold or modal)
const ScrollProgress = lazy(() => import("@/components/home/ScrollProgress"));
const ExitIntentModal = lazy(() => import("@/components/home/ExitIntentModal"));
const InlineContactForm = lazy(() => import("@/components/home/InlineContactForm"));

const metrics = [
  { value: "25,000+", label: "Founders Reached", icon: Users, description: "through talks, trainings & advisory" },
  { value: "$700M+", label: "Raised by Portfolio", icon: TrendingUp, description: "by startups I supported" },
  { value: "50+", label: "Products Built", icon: Briefcase, description: "from zero to scale" },
];

const services = [
  {
    number: "01",
    title: "Trainings",
    description: "Product and innovation workshops for scale-ups and corporates seeking to level up their teams.",
    href: "/services/trainings",
    icon: GraduationCap,
    accent: "from-primary/20 to-primary/5",
  },
  {
    number: "02",
    title: "Keynotes",
    description: "Inspiring talks for conferences and corporate events on product, innovation, and scaling.",
    href: "/services/keynotes",
    icon: Mic,
    accent: "from-blue-500/20 to-blue-500/5",
  },
  {
    number: "03",
    title: "Rapid Response",
    description: "Crisis intervention when the house is on fire. We bring experienced operators who silently solve tough problems.",
    href: "/services/rapid-response",
    icon: Zap,
    accent: "from-urgent/20 to-urgent/5",
  },
];

const companies = ["Konux", "LangFuse", "SimBricks", "Personio", "CedarDB", "SuperGlue"];

const testimonials: TestimonialItem[] = [
  {
    quote: "Bastian helped us rethink our entire product strategy in just two weeks. His founder experience combined with deep tech know-how is rare and incredibly valuable.",
    author: "Sarah M.",
    role: "CEO",
    company: "Series B SaaS",
    rating: 5,
  },
  {
    quote: "When our product stalled and we were burning runway, Bastian came in and helped us pivot quickly. We closed our next round 3 months later.",
    author: "Marcus K.",
    role: "Founder",
    company: "Deep Tech Startup",
    rating: 5,
  },
  {
    quote: "The training transformed how our team approaches product decisions. We went from endless debates to shipping with confidence.",
    author: "Julia R.",
    role: "VP Product",
    company: "DAX 40 Company",
    rating: 5,
  },
];

const Index = () => {
  return (
    <Layout>
      <Suspense fallback={null}>
        <ScrollProgress />
        <ExitIntentModal />
      </Suspense>
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        {/* Animated background - reduced blur for performance */}
        <GridBackground variant="gradient" className="opacity-60" />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/10 blur-[60px] animate-float" />
        <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full bg-primary/5 blur-[50px] animate-float" style={{ animationDelay: "1s" }} />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left content */}
            <div className="lg:col-span-7 animate-fade-up">
              {/* Floating badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">
                  From the <span className="text-primary">CDTM & TUM Venture Labs</span> ecosystem
                </span>
              </div>

              <p className="text-primary font-mono text-sm tracking-wider mb-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
                PRODUCT CONSULTING & ADVISORY
              </p>
              
              <TypewriterHeadline
                as="h1"
                text="I build products that scale."
                highlightText="that scale."
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
                showCursor="always"
                typeSpeed={40}
              />
              <p className="text-muted-foreground text-3xl md:text-4xl lg:text-5xl mb-6">From zero to $700M raised.</p>
              
              <p className="text-lg text-foreground/80 mb-3 font-medium animate-fade-in" style={{ animationDelay: "0.4s" }}>
                Product stalling? Running out of runway? Team struggling to ship?
              </p>
              
              <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-8">
                PhD-trained founder and former CPO helping scale-ups and corporates 
                navigate product strategy, team building, and growth challenges.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" aria-label="Scroll to contact section to book a free consultation call">
                  <Button size="lg" className="font-semibold text-base px-8 hover:scale-105 transition-transform">
                    Book a Free Call
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </a>
                <Link to="/about" aria-label="Learn more about Bastian Burger's consulting experience and background">
                  <Button variant="outline" size="lg" className="font-medium group">
                    Learn More
                    <ArrowRight className="ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" size={16} />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right side - Headshot with effects */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end animate-fade-in" style={{ animationDelay: "0.5s" }}>
              <div className="relative">
                {/* Glow ring behind - reduced blur */}
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl scale-110 animate-glow-pulse" />
                
                {/* Headshot placeholder */}
                <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-full border-2 border-primary/30 bg-card/80 flex items-center justify-center shadow-glow">
                  <User className="w-24 h-24 md:w-32 md:h-32 text-muted-foreground/20" />
                  
                  {/* Rotating ring */}
                  <div className="absolute inset-[-4px] rounded-full border border-dashed border-primary/30 animate-spin" style={{ animationDuration: "20s" }} />
                </div>
                
                {/* Decorative rings */}
                <div className="absolute inset-0 rounded-full border border-primary/10 scale-125" />
                <div className="absolute inset-0 rounded-full border border-primary/5 scale-150" />
                
                {/* Floating testimonial card */}
                <div className="absolute -bottom-4 -left-12 md:-left-20 max-w-[220px] animate-fade-in" style={{ animationDelay: "1s" }}>
                  <GlowCard className="p-4">
                    <p className="text-xs text-foreground/80 italic mb-2 leading-relaxed">
                      "Helped us pivot and close our next round in 3 months."
                    </p>
                    <p className="text-[10px] text-muted-foreground font-medium">— Marcus K., Founder</p>
                  </GlowCard>
                </div>

                {/* Stats badge */}
                <div className="absolute -top-2 -right-4 md:-right-8 animate-fade-in" style={{ animationDelay: "1.2s" }}>
                  <div className="px-4 py-2 rounded-lg glass text-center">
                    <p className="text-lg font-bold text-primary">50+</p>
                    <p className="text-[10px] text-muted-foreground">Products</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <ScrollIndicator />
      </section>

      {/* Metrics Section - Enhanced */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-card/50 to-background" />
        <GridBackground variant="dots" className="opacity-20" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {metrics.map((metric, index) => (
              <div
                key={metric.label}
                className="relative group animate-fade-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="p-8 rounded-2xl border border-border bg-card/50 hover:border-primary/30 transition-all duration-300">
                  {/* Glow behind on hover */}
                  <div className="absolute inset-0 rounded-2xl bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity blur-xl -z-10" />
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <metric.icon className="text-primary" size={24} />
                    </div>
                    <div className="flex-1">
                      <AnimatedCounter 
                        value={metric.value} 
                        className="text-4xl font-bold text-foreground block mb-1"
                        showCursor
                        cursorDuration={2500}
                      />
                      <p className="text-sm font-medium text-foreground">{metric.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{metric.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Carousel */}
      <TestimonialCarousel testimonials={testimonials} />

      {/* Services Section - Enhanced */}
      <section className="py-32 relative overflow-hidden">
        <GridBackground variant="grid" className="opacity-10" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16 animate-fade-up">
            <p className="text-primary font-mono text-sm tracking-wider mb-4">SERVICES</p>
            <TypewriterHeadline
              as="h2"
              text="How I Can Help"
              className="text-3xl md:text-5xl font-bold mb-4"
              showCursor="typing"
              typeSpeed={30}
            />
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Three ways to work together, each designed to deliver tangible results 
              for your organization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div 
                key={service.title} 
                className="animate-fade-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Link to={service.href} className="block h-full" aria-label={`Learn more about ${service.title} service`} title={service.description}>
                  <GlowCard className="h-full">
                    <div className={`p-8 bg-gradient-to-br ${service.accent} rounded-xl h-full`}>
                      {/* Number badge */}
                      <div className="number-badge mb-6">
                        {service.number}
                      </div>
                      
                      {/* Icon */}
                      <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <service.icon className="text-primary" size={28} />
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                      <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      
                      <span className="text-primary text-sm font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                        Learn more 
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </GlowCard>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logo Strip - Enhanced */}
      <LogoStrip companies={companies} />

      {/* Bio Teaser - Enhanced */}
      <section className="py-32 relative overflow-hidden">
        <GridBackground variant="gradient" className="opacity-40" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto animate-fade-up">
            <div className="text-center mb-12">
              <p className="text-primary font-mono text-sm tracking-wider mb-4">ABOUT</p>
              <TypewriterHeadline
                as="h2"
                text="From PhD to Founder to CPO"
                className="text-3xl md:text-5xl font-bold mb-6"
                showCursor="typing"
                typeSpeed={35}
              />
            </div>
            
            <div className="glass rounded-2xl p-8 md:p-12">
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 text-center">
                I've worn many hats: researcher, founder, product leader—and trained 25,000+ founders 
                through the CDTM and TUM Venture Labs ecosystem. This unique journey gives me the 
                ability to bridge academia, deep tech, and scale.
              </p>
              
              <div className="flex justify-center">
                <Link to="/about" aria-label="Read about Bastian Burger's journey from PhD to Founder to CPO">
                  <Button variant="outline" className="font-medium hover:scale-105 transition-transform">
                    Read My Story
                    <ArrowRight className="ml-2" size={16} />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - lazy loaded with form libs */}
      <Suspense fallback={<div className="py-32 bg-card/30" />}>
        <InlineContactForm calendlyUrl="https://calendar.app.google/sUsX1ogwnqf6zN33A" />
      </Suspense>
    </Layout>
  );
};

export default Index;
